'use client'

import * as Accordion from '@radix-ui/react-accordion'
import { useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import { useBus, useListener } from 'react-bus'

import { useRouter } from '@/i18n-routing'
import { getStore } from '@/store'

import {
	captureBuilderEvent,
	STEP_TO_INDEX,
	STEP_TO_NAME,
} from '../builderAnalytics'
import {
	CLOSE_CONTENT_ACCORDIONS_EVENT,
	OPEN_CONTENT_ACCORDIONS_EVENT,
} from '../contentFormConstants'
import {
	QR_NAME_FORM_ID,
	qrNameAtom,
	TContentFormHandle,
	TQrContentFormSection,
	WELCOME_SCREEN_FORM_ID,
} from '../contentFormSections'
import { resetContentFormSections } from '../contentFormSections/resetContentFormSections'
import { ATTEMPT_SAVE_EDITED_CONTENT_FORM_EVENT } from '../GeneratorLayout/GeneratorFooter/EditModeGeneratorFooter'
import {
	NEXT_BUTTON_CLICK_EVENT,
	RESET_NEXT_BUTTON_LOADING_EVENT,
} from '../GeneratorLayout/GeneratorFooter/NextButton'
import {
	activeContentTypeAtom,
	isContentTouchedAtom,
	isDemoModeAtom,
	qrTypeAtom,
	qrUriAtom,
} from '../generatorState'
import { QR_TYPE, TContentFormProps } from '../generatorTypes'
import { qrDesignAtom } from '../pages/QrDesignFormPage/qrDesignState'
import { QR_TYPE_TO_CONTENT_ATOM } from '../utils/qrTypeToContentAtom'
import { ContentFormSection } from './ContentFormSection'
import { submitContentForm } from './contentFormUtils'
import { useCreateQrOnFirstValid } from './hooks/useCreateQrOnFirstValid'
import { useDisableDemoModeOnTouch } from './hooks/useDisableDemoModeOnTouch'
import { useSaveContentForm } from './hooks/useSaveContentForm'
import { useUpdateQrOnContentChange } from './hooks/useUpdateQrOnContentChange'
import { useValidateContentForm } from './hooks/useValidateContentForm'

const INITIALLY_COLLAPSED_FORM_ID_LIST = [
	WELCOME_SCREEN_FORM_ID,
	QR_NAME_FORM_ID,
]

export type TQrContentFormConfig = {
	formSectionList: TQrContentFormSection[]
}

export const ContentForm = ({
	config: { formSectionList },
	qrType,
	qrId,
	qrUri,
	qrName,
	qrContent,
	qrDesign,
	isChangeTypeMode,
}: {
	config: TQrContentFormConfig
	qrType: QR_TYPE
} & TContentFormProps) => {
	const t = useTranslations()
	const router = useRouter()
	const bus = useBus()

	const contentAtom = QR_TYPE_TO_CONTENT_ATOM[qrType]
	if (isChangeTypeMode && qrContent === undefined) {
		qrContent = getStore().get(contentAtom)
	}

	useHydrateAtoms(
		// @ts-expect-error QR data atoms are hydrated only if QR data is passed
		qrId && qrUri && qrContent
			? [
					[qrUriAtom, qrUri],
					[qrNameAtom, qrName],
					[contentAtom, qrContent],
					[qrDesignAtom, qrDesign],
					[isDemoModeAtom, false],
					[isContentTouchedAtom, true],
				]
			: [[isDemoModeAtom, true]],
	)

	const setQrType = useSetAtom(qrTypeAtom)
	const setActiveContentType = useSetAtom(activeContentTypeAtom)
	const setQrDesign = useSetAtom(qrDesignAtom)
	const setQrUri = useSetAtom(qrUriAtom)
	const setQrName = useSetAtom(qrNameAtom)
	const setContentAtom = useSetAtom(contentAtom)
	const setIsDemoMode = useSetAtom(isDemoModeAtom)
	const setIsContentTouched = useSetAtom(isContentTouchedAtom)

	useEffect(() => {
		setQrType(qrType)
		const wasAlreadyActiveType =
			getStore().get(activeContentTypeAtom) === qrType
		setActiveContentType(qrType)
		if (qrDesign) {
			setQrDesign(qrDesign)
		}
		if (qrId && qrUri && qrContent) {
			setQrUri(qrUri)
			setQrName(qrName)
			setContentAtom(qrContent)
			setIsDemoMode(false)
			setIsContentTouched(true)
		} else if (wasAlreadyActiveType) {
			// Returning to the same type after a Step 1 round trip preserves
			// content, but GeneratorPreview unconditionally forces demo mode on
			// while Step 1 is active (for the type-card hover preview). Restore
			// whatever this type's content was actually in before that — driven
			// by isContentTouchedAtom, not the demo flag Step 1 just clobbered.
			setIsDemoMode(!getStore().get(isContentTouchedAtom))
		} else {
			// The active type actually changed (e.g. returning from the homepage,
			// or any other non-QrTypeCard-click path) — clear stale Step 2 state
			// (color scheme, hex fields, etc.) from the previous type instead of
			// relying solely on QrTypeCard's click handler to have done it.
			//
			// Deliberately does NOT also reset contentFormValidityAtom here: this
			// effect runs after this type's sections have already mounted and
			// registered their real initial validity (React fires child effects
			// before the parent's), so resetting it now would wipe correct data
			// with nothing left to re-populate it.
			resetContentFormSections()
			setIsDemoMode(true)
			setIsContentTouched(false)
		}
	}, [
		qrDesign,
		qrType,
		qrId,
		qrUri,
		qrName,
		qrContent,
		setQrDesign,
		setQrType,
		setActiveContentType,
		setQrUri,
		setQrName,
		setContentAtom,
		setIsDemoMode,
		setIsContentTouched,
	])

	const [openSectionList, setOpenSectionList] = useState(
		formSectionList
			.filter(({ id }) => !INITIALLY_COLLAPSED_FORM_ID_LIST.includes(id))
			.map(({ id }) => id),
	)

	useDisableDemoModeOnTouch(contentAtom)

	const formRefs = useRef<
		Record<string, { order: number; ref: TContentFormHandle }>
	>({})

	const validateContentForm = useValidateContentForm({
		formRefs,
		formSectionList,
		setOpenSectionList,
	})

	const resetNextButtonLoading = () => {
		bus.emit(RESET_NEXT_BUTTON_LOADING_EVENT)
	}

	const createQrOnFirstValidAbortSignalRef = useRef<AbortController | null>(
		null,
	)

	useCreateQrOnFirstValid(createQrOnFirstValidAbortSignalRef, qrType)

	useUpdateQrOnContentChange(qrType)

	const handleAttemptSubmit = async () => {
		const isFormValid = await validateContentForm()
		if (isFormValid) {
			captureBuilderEvent('builder_step_complete', {
				step_index: STEP_TO_INDEX.content,
				step_name: STEP_TO_NAME.content,
				section: 'wizard_stepper',
			})
			await submitContentForm({
				qrType,
				router,
				resetNextButtonLoading,
				createQrOnFirstValidAbortSignalRef:
					createQrOnFirstValidAbortSignalRef,
				formRefs,
				t,
			})
		} else {
			resetNextButtonLoading()
		}
	}

	useListener(NEXT_BUTTON_CLICK_EVENT, handleAttemptSubmit)

	const saveContentForm = useSaveContentForm(qrType)

	const handleAttemptSave = async () => {
		const isFormValid = await validateContentForm()
		if (isFormValid) {
			await saveContentForm()
		}
		resetNextButtonLoading()
	}

	useListener(ATTEMPT_SAVE_EDITED_CONTENT_FORM_EVENT, handleAttemptSave)

	const handleCloseAccordions = (event: unknown) => {
		const closeAccordions = event as TQrContentFormSection['id'][]
		const accordions = formSectionList
			.filter(({ id }) => !closeAccordions.includes(id))
			.map(({ id }) => id)
		setOpenSectionList(accordions)
	}

	useListener(CLOSE_CONTENT_ACCORDIONS_EVENT, handleCloseAccordions)

	const handleOpenAccordions = (event: unknown) => {
		const openAccordions = event as TQrContentFormSection['id'][]
		const accordions = [...openSectionList, ...openAccordions]
		setOpenSectionList(accordions)
	}

	useListener(OPEN_CONTENT_ACCORDIONS_EVENT, handleOpenAccordions)

	return (
		<Accordion.Root
			type="multiple"
			value={openSectionList}
			onValueChange={setOpenSectionList}
			className="flex w-full flex-col gap-4"
		>
			{formSectionList.map(
				(
					{ id, title, description, icon, Content, disabled = false },
					index,
				) => {
					return (
						<ContentFormSection
							key={title}
							id={id}
							ref={(formHandle) => {
								if (formHandle && formRefs?.current) {
									formRefs.current[title] = {
										order: index,
										ref: formHandle,
									}
								}
							}}
							title={title}
							description={description}
							icon={icon}
							Content={Content}
							isOpen={openSectionList.includes(id)}
							disabled={disabled}
						/>
					)
				},
			)}
		</Accordion.Root>
	)
}
